import { Button, Col, Form, Row } from "antd";
import { InputField } from "../../../../../atoms/FormFields";
import { useForm, useWatch } from "react-hook-form";
import React, { useEffect, useRef, useState } from "react";
import { docSigning } from "../../../ducks/services";
import { SuccessMessage } from "../../../../../atoms/Toasts/SuccessMessage";
import { ErrorMessage } from "../../../../../atoms/Toasts/ErrorMessage";
import { AxiosError } from "axios";
import { RcFile } from "antd/es/upload";
import { getProposalDoc } from "../../../ducks/actions";
import { useAppDispatch } from "../../../../../config/hooks";

interface PropsType {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
  proposalDocumentData?: {
    email: string;
    first_name: string;
    id: string;
    last_name: string;
    phone: string;
    title: string;
  };
}

interface FormValues {
  first_name: string;
  last_name: string;
}

export default function TypeForm(props: PropsType) {
  const { setLoading, onClose, proposalDocumentData } = props;
  const dispatch = useAppDispatch();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const locationUrl = window.location.pathname;
  const pathSegments = locationUrl.split("/");
  const proposalId = pathSegments[pathSegments.length - 1];
  const [disableState, setDisableState] = useState(false);
  // const companyId = pathSegments[pathSegments.length - 1];
  // const dealId = pathSegments[pathSegments.length - 2];

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const firstName = useWatch({ control, name: "first_name" }) || "";
  const lastName = useWatch({ control, name: "last_name" }) || "";

  const fullName = `${firstName} ${lastName}`.trim();

  const trimCanvas = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return canvas;

    const { width, height } = canvas;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    let top = null,
      bottom = null,
      left = null,
      right = null;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          if (top === null) top = y;
          if (left === null || x < left) left = x;
          if (right === null || x > right) right = x;
          bottom = y;
        }
      }
    }

    if (top === null || bottom === null || left === null || right === null) {
      return canvas; // No content found
    }

    const trimmedWidth = right - left + 1;
    const trimmedHeight = bottom - top + 1;

    const trimmedCanvas = document.createElement("canvas");
    trimmedCanvas.width = trimmedWidth;
    trimmedCanvas.height = trimmedHeight;
    const trimmedCtx = trimmedCanvas.getContext("2d");

    if (!trimmedCtx) return canvas;

    trimmedCtx.putImageData(
      ctx.getImageData(left, top, trimmedWidth, trimmedHeight),
      0,
      0
    );

    return trimmedCanvas;
  };

  const drawSignatureToCanvas = (text: string): string | null => {
    const canvas = canvasRef.current;
    if (!canvas || !text) return null;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = "36px 'Great Vibes', cursive";
    ctx.fillStyle = "#000";
    ctx.textBaseline = "top";
    ctx.fillText(text, 10, 10);

    const trimmedCanvas = trimCanvas(canvas);
    return trimmedCanvas.toDataURL("image/png");
  };

  const getSignatureFile = (text: string): Promise<File | null> => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas || !text) return resolve(null);

      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve(null);

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = "36px 'Great Vibes', cursive";
      ctx.fillStyle = "#000";
      ctx.textBaseline = "top";
      ctx.fillText(text, 10, 10);

      const trimmedCanvas = trimCanvas(canvas);

      trimmedCanvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], "signature.png", { type: "image/png" });
          resolve(file);
        } else {
          resolve(null);
        }
      }, "image/png");
    });
  };

  const onSubmit = async (val: FormValues) => {
    setLoading(true);

    const signatureImage = drawSignatureToCanvas(
      `${val.first_name} ${val.last_name}`
    );
    console.log("Base64 Signature Image:", signatureImage);

    // Now you can send `signatureImage` to your API
    const payload = {
      ...val,
      signature: signatureImage, // base64 string
    };

    console.log("Sending to API:", payload);

    let signatureFile: File | RcFile | null = null;
    if (signatureImage) {
      signatureFile = await getSignatureFile(
        `${val.first_name} ${val.last_name}`
      );
    }

    if (!signatureFile) {
      setLoading(false);
      ErrorMessage("Signature file could not be generated.");
      return;
    }

    docSigning({
      owner_id: proposalId,
      image: signatureFile,
      // company_id: companyId,
      // deal_id: dealId,
      accepted_by_name: `${val.first_name} ${val.last_name}`,
      accepted_by_email: proposalDocumentData?.email || "",
      accepted_by_title: proposalDocumentData?.title || "",
    })
      .then((res) => {
        if (res) {
          setLoading(false);
          SuccessMessage("Document signed successfully.");
          dispatch(getProposalDoc(proposalId));
          onClose();
        }
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (proposalDocumentData?.first_name || proposalDocumentData?.last_name) {
      setValue("first_name", proposalDocumentData?.first_name || "");
      setValue("last_name", proposalDocumentData?.last_name || "");
      setDisableState(true);
    } else {
      setDisableState(false);
    }
  }, [proposalDocumentData, setValue]);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap"
        rel="stylesheet"
      />

      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      >
        <Row gutter={8}>
          <Col span={12}>
            <InputField
              fieldName="first_name"
              label="First name"
              control={control}
              defaultValue=""
              iProps={{
                placeholder: "Enter first name",
                disabled: disableState,
              }}
              isRequired
              rules={{ required: "First name is required." }}
              validate={errors.first_name && "error"}
              validMessage={errors.first_name && errors.first_name.message}
            />
          </Col>
          <Col span={12}>
            <InputField
              fieldName="last_name"
              label="Last name"
              control={control}
              defaultValue=""
              iProps={{
                placeholder: "Enter last name",
                disabled: disableState,
              }}
              isRequired
              rules={{ required: "Last name is required." }}
              validate={errors.last_name && "error"}
              validMessage={errors.last_name && errors.last_name.message}
            />
          </Col>

          <Col span={24}>
            <Form.Item label="Signature preview">
              <div
                style={{
                  minHeight: 100,
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "10px",
                  fontFamily: "'Great Vibes', cursive",
                  fontSize: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {fullName || "Signature will appear here"}
              </div>
            </Form.Item>
          </Col>

          <Col span={24}>
            <div style={{ display: "none" }}>
              {/* 👇 Hidden canvas to render image on submit */}
              <canvas width={500} height={150} ref={canvasRef} />
            </div>
          </Col>

          <Col span={24}>
            <div
              style={{
                paddingTop: "4px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button htmlType="submit" type="primary" className="mint-btn">
                Sign & accept
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </>
  );
}
